import * as PropTypes from 'prop-types';

import {
  CoreObjectArchivable,
  CoreObjectArchiveData,
} from 'app-core/platform/archivable/CoreObjectArchiveConstants';
import {DEPRECATED_FOLDER_SHAPE} from 'constants/DeprecatedFolderConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ArchiveRecordGraphQL} from '../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';

export enum ArchiveStatus {
  IS_ARCHIVED = 'IS_ARCHIVED',
  NOT_ARCHIVED = 'NOT_ARCHIVED',
}

// Keep these in sync with benchling/archive/constants.py
export const IS_ARCHIVED_TEXT = 'Archived';
export const NOT_ARCHIVED_TEXT = 'Not archived';
export const OTHER_PURPOSE = 'Other';
export const UNINSTALLED_PURPOSE = 'Uninstalled';
export const EXPENDED_PURPOSE = 'Expended';
export const BATCH_SCHEMA_DEACTIVATED_PURPOSE = 'Batch schema deactivated by containable type change';

// Keep in sync with `archive_record_filter.py`
export const NOT_ARCHIVED_PURPOSE_STRING = 'NOT_ARCHIVED';
export const NOT_ARCHIVED_PURPOSE_TEXT = 'Not archived';

export const ALL_ARCHIVED_PURPOSE_STRING = 'ALL_ARCHIVED';
export const ALL_ARCHIVED_PURPOSE_ALT_TEXT = 'All archived';
export const ALL_ARCHIVED_PURPOSE_TEXT = 'Archived';

// Keep in sync with `archive_record_filter.py`
// In non-capillary, ANY_ARCHIVED represents all items, regardless of archive status.
export const ANY_ARCHIVED_PURPOSE_STRING = 'ANY_ARCHIVED';

// In capillary and v3, ANY_ARCHIVED represents all archived items, regardless of purpose.
export const CAPILLARY_ANY_ARCHIVED_PURPOSE_STRING = 'ANY_ARCHIVED';

export interface ArchiveRecord {
  purpose: string;
}

export const ARCHIVE_RECORD_SHAPE = PropTypes.shape({
  purpose: PropTypes.string.isRequired,
});

export interface ArchivableItemREST {
  id: string;
  name: string;
  archiveRecord?: ArchiveRecord | null;
}

export interface ArchivableItemGQL {
  id: string;
  name: string;
  archiveRecord: ArchiveRecordGraphQL | null;
}

/**
 * Serves as a "funnel" type, which can accept any of the three iterations of archivable items: Legacy REST,
 * Legacy GraphQL, and modern Core Object. Useful for building a unified interface to build entrypoints into
 * Archive module behavior and type predicates for narrowing the type further.
 */
export interface MaybeArchivableItem {
  id: string;
  name: string;
  archiveRecord?: ArchiveRecord | ArchiveRecordGraphQL | null;
  archiveData?: CoreObjectArchiveData | null;
}

export function isArchiveRecordGraphQL(
  archiveRecord?: ArchiveRecord | ArchiveRecordGraphQL | null
): archiveRecord is ArchiveRecordGraphQL {
  return Boolean(
    archiveRecord &&
    archiveRecord.hasOwnProperty('__typename') &&
    (archiveRecord as {__typename: string}).__typename === 'ArchiveRecordNode'
  );
}

export type ArchivableItem = ArchivableItemREST | ArchivableItemGQL | CoreObjectArchivable;

export interface BulkArchiveItemsResponse {
  archivedItemIds: Array<string>;
}

export interface BulkUnarchiveItemsResponse {
  unarchivedItemIds: Array<string>;
}

export const ARCHIVABLE_ITEM_REST_PROPS = {
  archiveRecord: ARCHIVE_RECORD_SHAPE,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  projectFolder: DEPRECATED_FOLDER_SHAPE,
};

export interface ItemForArchive {
  friendlyTypeName: string;
  iconId: BenchlingIconName;
  id: string;
  isRegistered: boolean;
  isShortcut: boolean;
  isProjectRootFolder: boolean;
  name: string;
}

export interface ArchiveOptions {
  shouldRemoveBarcodes: boolean;
  shouldRemoveShortcuts: boolean | null;
  shouldUnlinkParts: boolean | null;
  shouldUnregisterBioentities: boolean | null;
  unregisteredBioentitiesFolderId: string | null;
  shouldArchiveContainedSamples: boolean | null;
  resultPurpose?: string | null;
}
